/**
 * ============================================================
 * TEACHER LIVE MONITOR
 * ============================================================
 * 
 * Real-time monitoring of active exam sessions:
 * - Active students
 * - Time remaining
 * - Violations
 * - System status
 * 
 * ============================================================
 */

import React, { useState, useEffect } from "react";
import { ref, onValue, get } from "firebase/database";
import { db } from "../services/firebase";
import {
  FaEye,
  FaClock,
  FaUser,
  FaDesktop,
  FaWifi,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBan,
  FaSync,
} from "react-icons/fa";
import "./TeacherLiveMonitor.css";

export default function TeacherLiveMonitor({ teacherId }) {
  const [activeSessions, setActiveSessions] = useState([]);
  const [credentials, setCredentials] = useState({});
  const [attempts, setAttempts] = useState({});
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load credentials
        const credsRef = ref(db, "studentCredentials");
        const credsSnap = await get(credsRef);
        setCredentials(credsSnap.exists() ? credsSnap.val() : {});

        // Load attempts
        const attemptsRef = ref(db, "studentAttempts");
        onValue(attemptsRef, (snap) => {
          setAttempts(snap.val() || {});
        });

        setLoading(false);
      } catch (err) {
        console.error("Error loading monitor data:", err);
        setLoading(false);
      }
    };

    loadData();

    // Auto-refresh every 5 seconds
    if (autoRefresh) {
      const interval = setInterval(loadData, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  useEffect(() => {
    // Combine credentials and attempts to find active sessions
    const sessions = Object.entries(credentials)
      .map(([studentId, cred]) => {
        const attempt = Object.values(attempts).find(
          (a) => a.loginId === studentId
        );

        if (!attempt || attempt.status !== "in_progress") {
          return null;
        }

        const now = Date.now();
        const startedAt = attempt.startedAt || now;
        const elapsed = Math.floor((now - startedAt) / 1000);

        // Calculate remaining time
        let remainingTime = null;
        if (cred.expiresAt) {
          remainingTime = Math.max(0, Math.floor((cred.expiresAt - now) / 1000));
        } else if (cred.endTime) {
          remainingTime = Math.max(
            0,
            Math.floor((new Date(cred.endTime).getTime() - now) / 1000)
          );
        }

        return {
          studentId,
          credential: cred,
          attempt,
          elapsed,
          remainingTime,
          status: cred.session?.active ? "active" : "inactive",
          device: cred.session?.device || "Unknown",
          ip: cred.session?.ip || "Unknown",
        };
      })
      .filter((s) => s !== null);

    setActiveSessions(sessions);
  }, [credentials, attempts]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return "N/A";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#22c55e";
      case "inactive":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className="tds-live-monitor-view">
        <div className="tds-loading">Loading live monitor...</div>
      </div>
    );
  }

  return (
    <div className="tds-live-monitor-view">
      <div className="tds-monitor-header">
        <div>
          <h2>
            <FaEye /> Live Test Monitor
          </h2>
          <p>
            Active Sessions: {activeSessions.length} | Last Updated:{" "}
            {new Date().toLocaleTimeString()}
          </p>
        </div>
        <div className="tds-monitor-controls">
          <button
            className={`tds-refresh-btn ${autoRefresh ? "active" : ""}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <FaSync /> {autoRefresh ? "Auto-Refresh ON" : "Auto-Refresh OFF"}
          </button>
        </div>
      </div>

      {activeSessions.length === 0 ? (
        <div className="tds-no-active-sessions">
          <FaCheckCircle />
          <h3>No Active Sessions</h3>
          <p>No students are currently taking the exam.</p>
        </div>
      ) : (
        <div className="tds-sessions-grid">
          {activeSessions.map((session) => {
            const details = session.attempt?.details || {};
            const statusColor = getStatusColor(session.status);

            return (
              <div key={session.studentId} className="tds-session-card">
                <div className="tds-session-header">
                  <div className="tds-session-status">
                    <div
                      className="tds-status-dot"
                      style={{ backgroundColor: statusColor }}
                    />
                    <span style={{ color: statusColor }}>
                      {session.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="tds-session-id">
                    <code>{session.studentId}</code>
                  </div>
                </div>

                <div className="tds-session-body">
                  <div className="tds-session-student">
                    <FaUser />
                    <div>
                      <strong>{details.name || "Unknown"}</strong>
                      <span>
                        {details.rollNo || details.universityRoll || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="tds-session-timer">
                    <div className="tds-timer-item">
                      <FaClock />
                      <div>
                        <div className="tds-timer-label">Time Elapsed</div>
                        <div className="tds-timer-value">
                          {formatTime(session.elapsed)}
                        </div>
                      </div>
                    </div>
                    <div className="tds-timer-item">
                      <FaClock />
                      <div>
                        <div className="tds-timer-label">Time Remaining</div>
                        <div
                          className={`tds-timer-value ${
                            session.remainingTime < 300 ? "warning" : ""
                          }`}
                        >
                          {formatTime(session.remainingTime)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="tds-session-info">
                    <div className="tds-info-item">
                      <FaDesktop />
                      <span>{session.device}</span>
                    </div>
                    <div className="tds-info-item">
                      <FaWifi />
                      <span>{session.ip}</span>
                    </div>
                    <div className="tds-info-item">
                      <strong>Test:</strong>{" "}
                      {session.credential.testType || "DEFAULT"}
                    </div>
                  </div>
                </div>

                <div className="tds-session-footer">
                  <span className="tds-session-started">
                    Started:{" "}
                    {new Date(session.attempt.startedAt).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
